import { Box, Chip, Typography } from "@mui/material";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import dayjs from "dayjs";
import SidePanel, { SidePanelSection, SidePanelKV, SidePanelStatGrid } from "./SidePanel";
import { colors } from "../theme";

const fmtTs = (v) => {
  if (!v) return null;
  const d = dayjs(v?.$date ?? v);
  return d.isValid() ? d.format("YYYY-MM-DD HH:mm:ss") : String(v);
};

const isFiniteNum = (v) => v != null && isFinite(v) && !isNaN(v);

// Legend swatch for the distribution bar — same shapes/colors as the trend chart's own legend
// (band/line/dash/dot), so a user who's seen the chart already knows what each mark means here.
const DistLegendItem = ({ color, kind, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    {kind === "dot" && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />}
    {kind === "band" && <Box sx={{ width: 14, height: 8, bgcolor: color, opacity: 0.5, borderRadius: 0.5, flexShrink: 0 }} />}
    {kind === "line" && <Box sx={{ width: 14, height: 2, bgcolor: color, flexShrink: 0 }} />}
    {kind === "dash" && <Box sx={{ width: 14, height: 2, background: `repeating-linear-gradient(90deg,${color} 0,${color} 3px,transparent 3px,transparent 6px)`, flexShrink: 0 }} />}
    <Typography sx={{ fontSize: 9.5, color: "rgba(255,255,255,0.55)", whiteSpace: "nowrap" }}>{label}</Typography>
  </Box>
);

// A single vertical reference tick on the distribution bar (min/max, fences, config limits).
const Tick = ({ left, color }) => (
  <Box sx={{ position: "absolute", top: -3, bottom: -3, left: `${left}%`, width: 2, backgroundColor: color }} />
);

// Mini range indicator — shows where this row's value sits relative to the full statistical
// picture: data min/max (grey, same as the chart's Min/Max reference lines), the IQR band and
// median/mean, the statistical fences (red) and, when configured, the device's own config limits
// (gold, same as the chart's Config Min/Max lines). The bar's own domain spans every one of these
// values (not just the fences) so min/max/config always have a visible tick, never clipped off
// the ends — and every color/shape is spelled out in the legend underneath.
const DistributionBar = ({ stats, configRange, value, outOfRange: outOfRangeProp }) => {
  if (!stats || !isFiniteNum(value)) return null;
  const hasConfig = configRange && (isFiniteNum(configRange.min) || isFiniteNum(configRange.max));
  const candidates = [stats.min, stats.max, stats.lower_fence, stats.upper_fence, value].filter(isFiniteNum);
  if (hasConfig) {
    if (isFiniteNum(configRange.min)) candidates.push(configRange.min);
    if (isFiniteNum(configRange.max)) candidates.push(configRange.max);
  }
  if (candidates.length < 2) return null;
  const rawLo = Math.min(...candidates);
  const rawHi = Math.max(...candidates);
  if (rawHi <= rawLo) return null;
  const pad = (rawHi - rawLo) * 0.04;
  const domLo = rawLo - pad;
  const domHi = rawHi + pad;
  const pct = (v) => Math.min(100, Math.max(0, ((v - domLo) / (domHi - domLo)) * 100));

  const outOfRange = outOfRangeProp != null ? outOfRangeProp : (
    hasConfig
      ? (isFiniteNum(configRange.min) && value < configRange.min) || (isFiniteNum(configRange.max) && value > configRange.max)
      : (isFiniteNum(stats.lower_fence) && value < stats.lower_fence) || (isFiniteNum(stats.upper_fence) && value > stats.upper_fence)
  );
  const markerColor = outOfRange ? "#ff4444" : "#4cceac";

  return (
    <Box sx={{ px: 0.5, pt: 2.5, pb: 0.5 }}>
      <Box sx={{ position: "relative", height: 4, borderRadius: 2, backgroundColor: "rgba(255,255,255,0.1)" }}>
        {isFiniteNum(stats.q1) && isFiniteNum(stats.q3) && (
          <Box sx={{
            position: "absolute", top: 0, bottom: 0,
            left: `${pct(stats.q1)}%`, width: `${Math.max(0, pct(stats.q3) - pct(stats.q1))}%`,
            backgroundColor: "#7b5fe0", opacity: 0.6, borderRadius: 2,
          }} />
        )}
        {isFiniteNum(stats.min) && <Tick left={pct(stats.min)} color="#6e6e7e" />}
        {isFiniteNum(stats.max) && <Tick left={pct(stats.max)} color="#6e6e7e" />}
        {isFiniteNum(stats.lower_fence) && <Tick left={pct(stats.lower_fence)} color="#ff4444" />}
        {isFiniteNum(stats.upper_fence) && <Tick left={pct(stats.upper_fence)} color="#ff4444" />}
        {hasConfig && isFiniteNum(configRange.min) && <Tick left={pct(configRange.min)} color="#b89400" />}
        {hasConfig && isFiniteNum(configRange.max) && <Tick left={pct(configRange.max)} color="#b89400" />}
        {isFiniteNum(stats.mean) && (
          <Box sx={{
            position: "absolute", top: -2, bottom: -2, left: `${pct(stats.mean)}%`, width: 2,
            background: "repeating-linear-gradient(180deg,#aaaaff 0,#aaaaff 2px,transparent 2px,transparent 4px)",
          }} />
        )}
        {isFiniteNum(stats.median) && (
          <Box sx={{ position: "absolute", top: -2, bottom: -2, left: `${pct(stats.median)}%`, width: 2, backgroundColor: "#33c2c2" }} />
        )}
        <Box sx={{
          position: "absolute", top: -4, left: `${pct(value)}%`, transform: "translateX(-50%)",
          width: 12, height: 12, borderRadius: "50%", backgroundColor: markerColor,
          border: "2px solid #161616", boxShadow: `0 0 6px ${markerColor}88`,
        }} />
      </Box>

      {/* Domain bounds, so the axis itself is legible without a separate lookup */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
        <Typography sx={{ fontSize: 9, color: "rgba(255,255,255,0.45)", fontFamily: "monospace" }}>{fmtNum(domLo)}</Typography>
        <Typography sx={{ fontSize: 9, color: "rgba(255,255,255,0.45)", fontFamily: "monospace" }}>{fmtNum(domHi)}</Typography>
      </Box>

      {/* Legend — spells out what each color/shape means */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, justifyContent: "center", mt: 1 }}>
        <DistLegendItem color="#6e6e7e" kind="line" label="Min / Max" />
        <DistLegendItem color="#7b5fe0" kind="band" label="IQR (Q1–Q3)" />
        <DistLegendItem color="#33c2c2" kind="line" label="Median" />
        <DistLegendItem color="#aaaaff" kind="dash" label="Mean" />
        <DistLegendItem color="#ff4444" kind="line" label="Fences" />
        {hasConfig && <DistLegendItem color="#b89400" kind="line" label="Config limit" />}
        <DistLegendItem color={markerColor} kind="dot" label="This value" />
      </Box>

      <Typography sx={{ fontSize: 10, color: outOfRange ? "#ff4444" : "#4cceac", fontWeight: 700, mt: 0.8, textAlign: "center" }}>
        {outOfRange ? "Outside range" : "Within range"}
      </Typography>
    </Box>
  );
};

// Endpoint card (Source / Destination) — same look as LogSidePanel
const EndpointBlock = ({ title, accentIcon, accentColor, ip, port, hostnames }) => {
  const hasHostname = hostnames && Object.values(hostnames).some(Boolean);
  if (!ip && (port == null || port === "") && !hasHostname) return null;
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1 }}>
        {accentIcon}
        <Typography sx={{
          fontSize: 11, fontWeight: 700, color: accentColor,
          textTransform: "uppercase", letterSpacing: 0.8,
        }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{
        p: 1.4, borderRadius: 1, backgroundColor: "#1c1c1c",
        border: `1px solid ${colors.grey[800]}`,
      }}>
        <SidePanelKV label="IP"   value={ip}  mono copyable />
        <SidePanelKV label="Port" value={port != null && port !== "" ? String(port) : null} mono />
        {hostnames && (
          <>
            <SidePanelKV label="DNS"     value={hostnames.dns_name}     mono copyable />
            <SidePanelKV label="NetBIOS" value={hostnames.netbios_name} mono copyable />
            <SidePanelKV label="Custom"  value={hostnames.custom_name} />
          </>
        )}
      </Box>
    </Box>
  );
};

const rwChip = (isWrite) => {
  if (isWrite === true)
    return <Chip label="WRITE" size="small" sx={{ bgcolor: "#4254fb22", color: "#7b8bff", fontSize: 10, height: 20, fontWeight: 700, letterSpacing: 0.6 }} />;
  if (isWrite === false)
    return <Chip label="READ" size="small" sx={{ bgcolor: "#4cceac22", color: "#4cceac", fontSize: 10, height: 20, fontWeight: 700, letterSpacing: 0.6 }} />;
  return null;
};

const anomalyChip = (row, mode) => {
  const val = mode === "outOfRange" ? row.outOfRange : row.anomaly;
  if (val !== true) return null;
  const label =
    mode === "outOfRange" ? "OUT OF RANGE" :
    mode === "externalIp" ? "EXTERNAL IP"  :
    "ANOMALY";
  return (
    <Chip
      icon={<WarningAmberIcon sx={{ fontSize: "14px !important", color: "#ffb422 !important" }} />}
      label={label}
      size="small"
      sx={{ bgcolor: "#ffb42222", color: "#ffb422", fontSize: 10, height: 20, fontWeight: 700, letterSpacing: 0.6 }}
    />
  );
};

const fmtNum = (v) => (v != null && isFinite(v) ? (Number.isInteger(Number(v)) ? String(v) : Number(v).toFixed(2)) : null);

const OtSidePanel = ({ row, open, onClose, anomalyMode = null, stats = null, configRange = null }) => {
  if (!row) {
    return <SidePanel open={open} onClose={onClose} title="OT event" width={780} />;
  }

  const title =
    row.operation ||
    [row.protocol, row.address].filter(Boolean).join(" · ") ||
    "OT event";
  const ts = fmtTs(row.timestamp);
  const subtitle = [
    ts,
    row.protocol ? row.protocol.toUpperCase() : null,
  ].filter(Boolean).join(" · ");

  const extra = row.extra && typeof row.extra === "object" ? row.extra : null;
  const extraEntries = extra ? Object.entries(extra).filter(([, v]) => v != null && v !== "") : [];

  const actions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
      {anomalyChip(row, anomalyMode)}
      {rwChip(row.is_write)}
    </Box>
  );

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      icon={<PrecisionManufacturingIcon sx={{ fontSize: 22 }} />}
      title={title}
      subtitle={subtitle}
      actions={actions}
      width={780}
    >
      {/* ── Endpoints ────────────────────────────────────────────── */}
      <SidePanelSection title="Endpoints">
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <EndpointBlock
            title="Source"
            accentIcon={<CallMadeIcon sx={{ fontSize: 16, color: "#ff9966" }} />}
            accentColor="#ff9966"
            ip={row.src_ip}
            port={row.src_port}
            hostnames={{
              dns_name:     row.dnsNameSrc     || row.src_hostnames?.dns_name,
              netbios_name: row.netBiosNameSrc || row.src_hostnames?.netbios_name,
              custom_name:  row.customNameSrc  || row.src_hostnames?.custom_name || row.hostNameSrc,
            }}
          />
          <EndpointBlock
            title="Destination"
            accentIcon={<CallReceivedIcon sx={{ fontSize: 16, color: "#7b8bff" }} />}
            accentColor="#7b8bff"
            ip={row.dest_ip}
            port={row.dest_port}
            hostnames={{
              dns_name:     row.dnsNameDst     || row.dest_hostnames?.dns_name,
              netbios_name: row.netBiosNameDst || row.dest_hostnames?.netbios_name,
              custom_name:  row.customNameDst  || row.customNameDest || row.dest_hostnames?.custom_name || row.hostNameDest,
            }}
          />
        </Box>
      </SidePanelSection>

      {/* ── Network Info ─────────────────────────────────────────── */}
      {(row.protocol || row.transport) && (
        <SidePanelSection title="Network Info">
          <SidePanelKV label="Protocol"  value={row.protocol} />
          <SidePanelKV label="Transport" value={row.transport} />
        </SidePanelSection>
      )}

      {/* ── Operation ────────────────────────────────────────────── */}
      <SidePanelSection title="Operation">
        <SidePanelKV label="Operation" value={row.operation} />
        <SidePanelKV label="Address"   value={row.address}   mono copyable />
        <SidePanelKV label="Data Type" value={row.data_type} />
        <SidePanelKV label="Value"     value={row.value != null && row.value !== "" ? String(row.value) : null} mono copyable />
        <SidePanelKV label="Hex"       value={row.value_hex != null && row.value_hex !== "" ? (typeof row.value_hex === "object" ? JSON.stringify(row.value_hex) : String(row.value_hex)) : null} mono copyable />
        <SidePanelKV label="Quantity"  value={row.quantity != null && row.quantity !== "" ? String(row.quantity) : null} mono />
        <SidePanelKV label="Status"    value={row.status} />
      </SidePanelSection>

      {/* ── Statistical Reference (period stats for this signal, from the trend chart) ──── */}
      {stats && stats.count > 0 && (
        <SidePanelStatGrid
          title="Statistical Reference"
          tiles={[
            { label: "Samples",     value: String(stats.count) },
            { label: "Min",         value: fmtNum(stats.min), color: "#6e6e7e" },
            { label: "Q1",          value: fmtNum(stats.q1), color: "#7b5fe0" },
            { label: "Median",      value: fmtNum(stats.median), color: "#33c2c2" },
            { label: "Mean",        value: fmtNum(stats.mean), color: "#aaaaff" },
            { label: "Q3",          value: fmtNum(stats.q3), color: "#7b5fe0" },
            { label: "Max",         value: fmtNum(stats.max), color: "#6e6e7e" },
            { label: "Std Dev",     value: fmtNum(stats.std_dev) },
            { label: "Lower Fence", value: fmtNum(stats.lower_fence), color: "#ff4444" },
            { label: "Upper Fence", value: fmtNum(stats.upper_fence), color: "#ff4444" },
            { label: "Config Min",  value: fmtNum(configRange?.min), color: "#b89400" },
            { label: "Config Max",  value: fmtNum(configRange?.max), color: "#b89400" },
          ]}
        >
          <DistributionBar stats={stats} configRange={configRange} value={Number(row.value)} outOfRange={row.outOfRange} />
        </SidePanelStatGrid>
      )}

      {/* ── Timing ───────────────────────────────────────────────── */}
      <SidePanelSection title="Timing">
        <SidePanelKV label="Timestamp" value={ts} mono />
        <SidePanelKV label="Response"  value={fmtTs(row.ts_resp)} mono />
      </SidePanelSection>

      {/* ── Extra (protocol-specific) ────────────────────────────── */}
      {extraEntries.length > 0 && (
        <SidePanelSection title="Protocol Extras">
          {extraEntries.map(([k, v]) => (
            <SidePanelKV
              key={k}
              label={k}
              value={typeof v === "object" ? JSON.stringify(v) : String(v)}
              mono
              copyable
            />
          ))}
        </SidePanelSection>
      )}
    </SidePanel>
  );
};

export default OtSidePanel;
